<template>
  <div class="marketing-page website-manager">
    <MarketingToolHeader
      eyebrow="Digital Experience"
      title="Website"
      description="Pflege Stellenanzeigen, Leistungen, Angebote und standortspezifische Inhalte für die Webseite."
      icon="mdi-web-box">
      <template v-slot:actions>
        <span class="studio-summary"><v-icon small>mdi-map-marker-multiple-outline</v-icon>{{ studioList.length }} Studios</span>
      </template>
    </MarketingToolHeader>

    <v-card dark flat class="navigation-surface">
      <div class="navigation-copy">
        <div class="surface-eyebrow">Arbeitsbereich</div>
        <h2>Website-Inhalte verwalten</h2>
        <p>Wähle den Bereich, den du bearbeiten möchtest.</p>
      </div>
      <v-tabs v-model="tab" dark show-arrows background-color="transparent" color="#58d388" slider-color="#009640" class="website-tabs">
        <v-tab v-for="item in tabItems" :key="item.label" class="website-tab">
          <v-icon left small>{{ item.icon }}</v-icon
          >{{ item.label }}
        </v-tab>
      </v-tabs>
    </v-card>

    <v-tabs-items v-model="tab" dark class="website-tab-items">
      <v-tab-item><website-job-offers /></v-tab-item>
      <v-tab-item><website-services /></v-tab-item>
      <v-tab-item><website-offers /></v-tab-item>

      <v-tab-item v-if="userService.hasRight('g_websitecontent')">
        <WebsiteVideoMigration :active="tab === 3" :refresh-key="videoRefresh" />
        <v-card dark flat class="content-surface">
          <div class="surface-header">
            <div>
              <div class="surface-eyebrow">Standort-Content</div>
              <h2>Studio auswählen</h2>
              <p>Header-Video, Clubbeschreibung und Bildergalerie pro Studio verwalten.</p>
            </div>
            <span class="result-pill">{{ filteredStudios.length }} Ergebnisse</span>
          </div>

          <v-text-field
            v-model="searchQuery"
            label="Studio suchen"
            outlined
            dense
            dark
            clearable
            hide-details
            prepend-inner-icon="mdi-magnify"
            class="modern-field studio-search" />

          <div v-if="filteredStudios.length" class="studio-grid">
            <v-card
              v-for="studio in filteredStudios"
              :key="studio._id"
              dark
              flat
              class="studio-card"
              role="button"
              tabindex="0"
              @click="openStudio(studio)"
              @keydown.enter="openStudio(studio)">
              <div class="studio-logo-wrap">
                <v-img
                  height="82"
                  contain
                  :src="`https://app.wellcomefit.ch/img/logos/studio/WELL-COME-FIT_LOGO_${studio.code.toUpperCase()}_BLACK.png`"
                  class="studio-logo">
                  <template v-slot:placeholder
                    ><div class="logo-placeholder">
                      <span>{{ getInitials(studio.name) }}</span>
                    </div></template
                  >
                </v-img>
              </div>
              <div class="studio-copy">
                <span class="studio-code">{{ studio.code }}</span>
                <h3>{{ studio.name }}</h3>
                <div><span>Content bearbeiten</span><v-icon small>mdi-arrow-right</v-icon></div>
              </div>
            </v-card>
          </div>

          <div v-else class="surface-empty">
            <span><v-icon>mdi-map-marker-off-outline</v-icon></span>
            <h3>Kein Studio gefunden</h3>
            <p>Passe den Suchbegriff an.</p>
            <v-btn v-if="searchQuery" text color="#58d388" class="clear-action" @click="searchQuery = ''">Suche zurücksetzen</v-btn>
          </div>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <WebsiteContentDialog v-if="showDialog" :selectedStudio="selectedStudio" @close-dialog="closeStudio" />
  </div>
</template>

<script>
import api from "@/api"
import WebsiteJobOffers from "./components/WebsiteJobOffers.vue"
import WebsiteContentDialog from "./components/WebsiteContentDialog.vue"
import WebsiteVideoMigration from "./components/WebsiteVideoMigration.vue"
import WebsiteOffers from "./components/WebsiteOffers.vue"
import WebsiteServices from "./components/WebsiteServices.vue"
import userService from "@/services/userService"
import MarketingToolHeader from "@/views/dashboard/communication/MarketingToolHeader.vue"

export default {
  components: { MarketingToolHeader, WebsiteVideoMigration, WebsiteContentDialog, WebsiteJobOffers, WebsiteOffers, WebsiteServices },
  data() {
    return {
      userService,
      studioList: [],
      tabItems: userService.hasRight("g_websitecontent")
        ? [
            { label: "Jobs", icon: "mdi-briefcase-outline" },
            { label: "Leistungen", icon: "mdi-dumbbell" },
            { label: "Angebote", icon: "mdi-tag-outline" },
            { label: "Content", icon: "mdi-image-multiple-outline" },
          ]
        : [
            { label: "Jobs", icon: "mdi-briefcase-outline" },
            { label: "Leistungen", icon: "mdi-dumbbell" },
            { label: "Angebote", icon: "mdi-tag-outline" },
          ],
      tab: 0,
      showDialog: false,
      selectedStudio: { name: "Bearbeiten", code: "", _id: "" },
      searchQuery: "",
      videoRefresh: 0,
    }
  },
  created() {
    this.fetchStudios()
  },
  computed: {
    studioListSorted() {
      return [...this.studioList].sort((a, b) => a.name.localeCompare(b.name))
    },
    filteredStudios() {
      if (!this.searchQuery) return this.studioListSorted
      const query = this.searchQuery.toLowerCase().trim()
      return this.studioListSorted.filter((studio) => studio.name.toLowerCase().includes(query))
    },
  },
  methods: {
    async fetchStudios() {
      try {
        const studios = await api.fetchStudios()
        this.studioList = studios.filter((item) => item.code !== "alibey" && item.code !== "ot")
      } catch (e) {
        this.$toast.error("Fehler beim Laden der Studios")
      }
    },
    getInitials(name) {
      return (name || "?")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("")
    },
    closeStudio() {
      this.showDialog = false
      this.videoRefresh++
    },
    openStudio(studio) {
      this.selectedStudio = { name: studio.name, code: studio.code, _id: studio._id }
      this.showDialog = true
    },
  },
}
</script>

<style scoped>
.marketing-page {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --text-soft: #d4d4d8;
  --muted: #a3a3a8;
  max-width: 92.5rem;
  margin: 0 auto;
  padding: 1.5rem;
  color: var(--text);
  font-size: 1rem;
}

.studio-summary,
.result-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 750;
}

.studio-summary {
  min-height: 2rem;
  gap: 0.4375rem;
  padding: 0.375rem 0.75rem;
  border: 1px solid rgba(0, 150, 64, 0.28);
  color: var(--text-soft);
  background: rgba(0, 150, 64, 0.1);
}

.studio-summary .v-icon {
  color: #58d388;
}

.navigation-surface,
.content-surface {
  border: 1px solid var(--border);
  border-radius: 1.375rem !important;
  background: rgba(255, 255, 255, 0.03) !important;
  box-shadow: 0 1rem 2.75rem rgba(0, 0, 0, 0.12);
}

.navigation-surface {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.5rem;
  margin: 1rem 0;
  padding: 1.125rem 1.375rem 0;
  overflow: hidden;
}

.navigation-copy {
  flex: 1 1 auto;
  padding-bottom: 1.125rem;
}

.surface-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.navigation-copy h2,
.surface-header h2 {
  margin: 0.1875rem 0 0;
  color: var(--text);
  font-size: 1.25rem;
}

.navigation-copy p,
.surface-header p,
.surface-empty p {
  margin: 0.25rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.5;
}

.website-tabs {
  flex: 0 1 auto;
  width: auto;
  max-width: 100%;
}

.website-tab {
  min-width: 7.5rem;
  min-height: 3.5rem;
  border-radius: 0.75rem 0.75rem 0 0;
  color: #b8b8bc !important;
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.website-tab.v-tab--active {
  color: #ffffff !important;
  background: rgba(0, 150, 64, 0.1);
}

.website-tab-items {
  overflow: visible;
  background: transparent !important;
}

.content-surface {
  padding: 1.375rem;
}

.surface-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.125rem;
  margin-bottom: 1rem;
}

.result-pill {
  min-height: 1.75rem;
  padding: 0.25rem 0.625rem;
  border: 1px solid var(--border);
  color: var(--text-soft);
  background: rgba(255, 255, 255, 0.055);
  white-space: nowrap;
}

.studio-search {
  width: min(100%, 28rem);
  margin-bottom: 1.125rem;
}

.modern-field ::v-deep .v-input__slot {
  min-height: 2.875rem !important;
  border-radius: 0.8125rem !important;
  background: rgba(255, 255, 255, 0.035) !important;
}

.modern-field ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.15) !important;
}

.modern-field ::v-deep .v-label {
  color: #b8b8bc !important;
  font-size: 0.875rem;
}

.studio-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
}

.studio-card {
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 1.0625rem !important;
  outline: none;
  background: var(--surface) !important;
  transition: transform 160ms ease, border-color 160ms ease, background-color 160ms ease;
}

.studio-card:focus-visible {
  border-color: rgba(0, 150, 64, 0.68);
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.studio-logo-wrap {
  display: grid;
  min-height: 7rem;
  place-items: center;
  padding: 0.875rem;
  background: rgba(255, 255, 255, 0.045);
}

.studio-logo {
  width: 100%;
}

.logo-placeholder {
  display: grid;
  width: 100%;
  height: 100%;
  place-items: center;
}

.logo-placeholder span {
  display: grid;
  width: 3.25rem;
  height: 3.25rem;
  place-items: center;
  border-radius: 1rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
  font-size: 0.875rem;
  font-weight: 800;
}

.studio-copy {
  padding: 0.875rem;
}

.studio-code {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.studio-copy h3,
.surface-empty h3 {
  margin: 0.25rem 0 0;
  overflow: hidden;
  color: var(--text);
  font-size: 1rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.studio-copy > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.875rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.075);
  color: var(--muted);
}

.studio-copy > div span {
  font-size: 0.875rem;
}

.studio-copy > div .v-icon {
  color: #58d388;
}

.surface-empty {
  display: flex;
  min-height: 17rem;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 2.5rem;
  text-align: center;
}

.surface-empty > span {
  display: grid;
  width: 3.5rem;
  height: 3.5rem;
  margin-bottom: 0.875rem;
  place-items: center;
  border-radius: 1rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.11);
}

.surface-empty > span .v-icon {
  color: currentColor;
}

.clear-action {
  min-height: 2.75rem;
  margin-top: 0.75rem;
  font-size: 0.875rem !important;
  text-transform: none;
}

@media (hover: hover) and (pointer: fine) {
  .studio-card:hover {
    transform: translateY(-2px);
    border-color: var(--border-strong);
    background: var(--surface-strong) !important;
  }
}

@media (max-width: 78.9375rem) {
  .marketing-page {
    padding: 1.125rem;
  }

  .navigation-surface {
    align-items: stretch;
    flex-direction: column;
    gap: 0;
  }

  .website-tabs {
    width: 100%;
  }

  .studio-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 59.9375rem) {
  .studio-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 37.4375rem) {
  .marketing-page {
    padding: 0.75rem;
  }

  .studio-summary {
    width: 100%;
    justify-content: center;
  }

  .navigation-surface {
    margin: 0.75rem 0;
    padding: 1rem 0.875rem 0;
    border-radius: 1.125rem !important;
  }

  .navigation-copy {
    padding: 0 0.125rem 0.875rem;
  }

  .website-tab {
    min-width: 6.5rem;
  }

  .content-surface {
    padding: 0.875rem;
    border-radius: 1.125rem !important;
  }

  .surface-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.625rem;
  }

  .studio-search {
    width: 100%;
  }

  .studio-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
